import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Search, X, CornerDownLeft, ArrowDown, ArrowUp, Loader2 } from "lucide-react";
import client from "@/lib/api";

function highlight(text, q) {
  if (!q || !text) return text;
  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-[rgba(58,160,255,0.35)] text-[var(--t-cyan)] px-0.5 rounded-sm">{text.slice(idx, idx + q.length)}</mark>
      {text.slice(idx + q.length)}
    </>
  );
}

function SearchModalInner({ onClose }) {
  const { i18n } = useTranslation();
  const [allPages, setAllPages] = useState([]);
  const [results, setResults] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const navigate = useNavigate();

  // Load suggestions (empty state) once per language
  useEffect(() => {
    let cancelled = false;
    client.get("/wiki/pages", { params: { language: i18n.language } }).then(r => {
      if (!cancelled) setAllPages(r.data);
    }).catch(() => {});
    const t = setTimeout(() => inputRef.current?.focus(), 30);
    return () => { cancelled = true; clearTimeout(t); };
  }, [i18n.language]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  // Debounced server search — triggered from onChange, not from effect-on-q
  const runSearch = (term) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!term) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await client.get("/wiki/search", { params: { q: term, limit: 10, language: i18n.language } });
        setResults(r.data);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 180);
  };

  // Cleanup pending debounce on unmount
  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  // Default suggestions when no query
  const display = useMemo(() => {
    if (!q.trim()) {
      return allPages.slice(0, 6).map(p => ({
        slug: p.slug, title: p.title, category: p.category, summary: p.summary,
      }));
    }
    return results;
  }, [q, results, allPages]);

  const safeActive = Math.min(active, Math.max(0, display.length - 1));

  const handleQueryChange = (e) => {
    const v = e.target.value;
    setQ(v);
    setActive(0);
    runSearch(v.trim());
  };

  const handleKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive(Math.min(safeActive + 1, display.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive(Math.max(safeActive - 1, 0));
    } else if (e.key === "Enter" && display[safeActive]) {
      e.preventDefault();
      navigate(`/wiki/${display[safeActive].slug}`);
      onClose();
    } else if (e.key === "Escape") {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh] px-4" data-testid="search-modal">
      <div className="absolute inset-0 bg-[#050912]/85 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl panel panel-glow corner-frame flex flex-col max-h-[80vh]">
        <div className="flex items-center gap-3 px-4 md:px-5 py-3 md:py-4 border-b border-[var(--t-border)]">
          {loading ? (
            <Loader2 className="w-5 h-5 text-[var(--t-blue-glow)] animate-spin flex-shrink-0" />
          ) : (
            <Search className="w-5 h-5 text-[var(--t-blue-glow)] flex-shrink-0" />
          )}
          <input
            ref={inputRef}
            value={q}
            onChange={handleQueryChange}
            onKeyDown={handleKey}
            placeholder="Search the codex — units, resources, mechanics..."
            className="flex-1 bg-transparent outline-none text-[var(--t-text)] placeholder:text-[var(--t-text-muted)] text-base md:text-lg min-w-0"
            data-testid="search-input"
            autoComplete="off"
            spellCheck="false"
          />
          <button onClick={onClose} className="w-8 h-8 inline-flex items-center justify-center text-[var(--t-text-dim)] hover:text-[var(--t-cyan)] flex-shrink-0" data-testid="search-close" aria-label="Close search">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto py-2">
          {!q.trim() && (
            <div className="px-5 pt-3 pb-1 font-mono text-[10px] tracking-[0.3em] text-[var(--t-text-muted)] uppercase">// suggested entries</div>
          )}

          {display.length === 0 && q.trim() && !loading ? (
            <div className="px-5 py-10 text-center" data-testid="search-empty">
              <div className="font-mono text-xs tracking-[0.3em] text-[var(--t-text-muted)] uppercase">// no entries match &ldquo;{q}&rdquo;</div>
              <div className="text-sm text-[var(--t-text-dim)] mt-2">Try a different keyword or browse the <button onClick={() => { navigate("/wiki"); onClose(); }} className="text-[var(--t-cyan)] underline">codex index</button>.</div>
            </div>
          ) : (
            <ul role="listbox" data-testid="search-results">
              {display.map((p, i) => (
                <li key={p.slug}>
                  <button
                    onClick={() => { navigate(`/wiki/${p.slug}`); onClose(); }}
                    onMouseEnter={() => setActive(i)}
                    className={`w-full text-left px-4 md:px-5 py-3 flex items-start gap-3 border-l-2 transition ${
                      i === safeActive
                        ? "bg-[rgba(58,160,255,0.08)] border-[var(--t-blue)]"
                        : "border-transparent hover:bg-[rgba(58,160,255,0.04)]"
                    }`}
                    data-testid={`search-result-${p.slug}`}
                  >
                    <Search className="w-4 h-4 mt-1 text-[var(--t-blue-glow)] flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="font-mono text-[10px] tracking-[0.25em] text-[var(--t-blue-glow)] uppercase mb-0.5 truncate">{p.category}</div>
                      <div className="font-display text-base text-[var(--t-cyan)] uppercase leading-tight">{highlight(p.title, q)}</div>
                      {(p.snippet || p.summary) && (
                        <div className="text-xs text-[var(--t-text-dim)] mt-1 line-clamp-2">
                          {highlight(p.snippet || p.summary, q)}
                        </div>
                      )}
                    </div>
                    {i === safeActive && <CornerDownLeft className="w-4 h-4 mt-1 text-[var(--t-cyan)] flex-shrink-0" />}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="hidden md:flex items-center justify-between px-5 py-2.5 border-t border-[var(--t-border)] text-[10px] font-mono tracking-[0.2em] text-[var(--t-text-muted)] uppercase">
          <div className="flex items-center gap-4">
            <span className="inline-flex items-center gap-1"><ArrowUp className="w-3 h-3" /><ArrowDown className="w-3 h-3" /> navigate</span>
            <span className="inline-flex items-center gap-1"><CornerDownLeft className="w-3 h-3" /> open</span>
            <span>esc to close</span>
          </div>
          <span>{q.trim() ? `${display.length} ${display.length === 1 ? "match" : "matches"}` : `${allPages.length} total entries`}</span>
        </div>
      </div>
    </div>
  );
}

export default function SearchModal({ open, onClose }) {
  if (!open) return null;
  return <SearchModalInner onClose={onClose} />;
}
